import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Paperclip, AlertCircle, Search, Loader2, Check, CheckCheck,
  Image as ImageIcon, FileText, X, Download,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  listConversations, listChatMessages, sendChatMessage,
  markConversationRead, searchChat, chatAttachmentUrl, getAdminUser,
} from "@/lib/api";

const POLL_MS = 6000;

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    const diffMs = today.getTime() - d.getTime();
    if (diffMs < 7 * 24 * 60 * 60 * 1000) {
      return d.toLocaleDateString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};
const fmtLastSeen = (iso) => {
  if (!iso) return "";
  try {
    const diffSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
    return `${Math.floor(diffSec / 86400)}d`;
  } catch { return ""; }
};

const isImageMime = (m) => (m || "").startsWith("image/");

const Avatar = ({ name, size = 36, active = false }) => (
  <span
    className="grid place-items-center rounded-full font-display font-bold shrink-0"
    style={{
      width: size, height: size,
      background: active ? "rgba(212,175,55,0.28)" : "rgba(212,175,55,0.18)",
      color: "#7A5E12",
      fontSize: size * 0.42,
    }}
  >
    {(name?.[0] || "?").toUpperCase()}
  </span>
);

const ConversationItem = ({ convo, active, onClick }) => {
  const last = convo.last_message;
  const lastBody = last?.body || (last?.attachment_name ? `📎 ${last.attachment_name}` : "Start a conversation");
  return (
    <button
      onClick={onClick}
      data-testid={`convo-${convo.peer.id}`}
      className="w-full text-left px-3 py-2.5 rounded-lg flex items-start gap-3 transition-colors"
      style={{ background: active ? "rgba(212,175,55,0.12)" : "transparent" }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#F8FAFC"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      <Avatar name={convo.peer.name} active={active} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold truncate" style={{ color: "#0B2545" }}>{convo.peer.name}</span>
          {last && <span className="text-[10px] shrink-0" style={{ color: "#94A3B8" }}>{fmtLastSeen(last.created_at)}</span>}
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="text-xs truncate" style={{ color: convo.unread > 0 ? "#0B2545" : "#64748B", fontWeight: convo.unread > 0 ? 600 : 400 }}>
            {lastBody}
          </span>
          {convo.unread > 0 && (
            <span
              className="text-[10px] font-bold px-1.5 rounded-full shrink-0"
              style={{ background: "#9B1C1C", color: "#FFFFFF", minWidth: 18, textAlign: "center" }}
              data-testid={`convo-unread-${convo.peer.id}`}
            >{convo.unread}</span>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#7A5E12" }}>{convo.peer.role}</span>
      </div>
    </button>
  );
};

const MessageBubble = ({ msg, isMe, peerName }) => {
  const hasFile = !!msg.attachment_file_id;
  const isImg = hasFile && isImageMime(msg.attachment_mime);
  return (
    <div className="flex flex-col gap-1" style={{ alignItems: isMe ? "flex-end" : "flex-start" }}>
      <div
        className="max-w-[78%] rounded-2xl px-3.5 py-2"
        style={{
          background: isMe ? "#0B2545" : "#F1F5F9",
          color: isMe ? "#FFFFFF" : "#0B2545",
          borderTopRightRadius: isMe ? 4 : undefined,
          borderTopLeftRadius: !isMe ? 4 : undefined,
          border: msg.is_urgent ? "2px solid #9B1C1C" : "none",
        }}
        data-testid={`msg-${msg.id}`}
      >
        {msg.is_urgent && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: isMe ? "#FFD774" : "#9B1C1C" }}>
            <AlertCircle className="h-3 w-3" /> Urgent
          </div>
        )}
        {msg.body && (
          <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ wordBreak: "break-word" }}>{msg.body}</p>
        )}
        {hasFile && (
          <div className={msg.body ? "mt-2" : ""}>
            {isImg ? (
              <a href={chatAttachmentUrl(msg.id)} target="_blank" rel="noreferrer" data-testid={`msg-${msg.id}-image`}>
                <img src={chatAttachmentUrl(msg.id)} alt={msg.attachment_name || "shared file"}
                     className="rounded-lg max-h-60 max-w-full"
                     style={{ display: "block" }} />
              </a>
            ) : (
              <a
                href={chatAttachmentUrl(msg.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5"
                style={{ background: isMe ? "rgba(255,255,255,0.12)" : "rgba(11,37,69,0.08)" }}
                data-testid={`msg-${msg.id}-file`}
              >
                <FileText className="h-3.5 w-3.5" />
                <span className="truncate max-w-[180px]">{msg.attachment_name}</span>
                <Download className="h-3 w-3 opacity-70" />
              </a>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 text-[10px]" style={{ color: "#94A3B8" }}>
        <span>{fmtTime(msg.created_at)}</span>
        {isMe && (
          msg.read_at
            ? <CheckCheck className="h-3 w-3" style={{ color: "#0F6D5D" }} />
            : <Check className="h-3 w-3" />
        )}
      </div>
    </div>
  );
};

export default function ChatPanel({ onSessionExpired }) {
  const me = getAdminUser();
  const [conversations, setConversations] = useState([]);
  const [activePeer, setActivePeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftFile, setDraftFile] = useState(null);
  const [urgent, setUrgent] = useState(false);
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const fileRef = useRef(null);
  const scrollerRef = useRef(null);
  const composerRef = useRef(null);

  const refreshConvos = async () => {
    try {
      const data = await listConversations();
      setConversations(data.conversations || []);
      // Pick a default active peer if none chosen yet
      setActivePeer((cur) => {
        if (cur) return cur;
        const first = (data.conversations || []).find((c) => c.peer.id !== me?.id);
        return first?.peer?.id || (data.conversations?.[0]?.peer?.id ?? null);
      });
    } catch (err) {
      if (err?.response?.status === 401) onSessionExpired?.();
    } finally { setLoadingConvos(false); }
  };

  const refreshMessages = async (peer, silent = false) => {
    if (!peer) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const data = await listChatMessages(peer, { limit: 100 });
      setMessages(data || []);
      // Mark read when viewing
      try { await markConversationRead(peer); } catch {}
    } catch (err) {
      if (err?.response?.status === 401) onSessionExpired?.();
    } finally { if (!silent) setLoadingMsgs(false); }
  };

  // Initial load
  useEffect(() => { refreshConvos(); /* eslint-disable-next-line */ }, []);

  // Reload messages when peer changes
  useEffect(() => { if (activePeer) refreshMessages(activePeer); /* eslint-disable-next-line */ }, [activePeer]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, activePeer]);

  // Polling — refresh active conversation + conversation list every POLL_MS
  useEffect(() => {
    const t = setInterval(() => {
      refreshConvos();
      if (activePeer) refreshMessages(activePeer, true);
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [activePeer]);

  const activeConvo = useMemo(
    () => conversations.find((c) => c.peer.id === activePeer),
    [conversations, activePeer],
  );

  const onSend = async () => {
    if (!activePeer) return;
    const body = draft.trim();
    if (!body && !draftFile) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("recipient", activePeer);
      if (body) fd.append("body", body);
      if (urgent) fd.append("is_urgent", "true");
      if (draftFile) fd.append("file", draftFile);
      await sendChatMessage(fd);
      setDraft(""); setDraftFile(null); setUrgent(false);
      if (fileRef.current) fileRef.current.value = "";
      await refreshMessages(activePeer, true);
      refreshConvos();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to send.";
      toast.error(msg);
    } finally {
      setSending(false);
      composerRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const doSearch = async () => {
    const q = query.trim();
    if (!q) { setSearchResults(null); return; }
    try {
      const data = await searchChat(q);
      setSearchResults(data || []);
    } catch { setSearchResults([]); }
  };

  return (
    <div className="space-y-4" data-testid="chat-panel">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>
          Internal Communication
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>Team Chat</h1>
        <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
          Secure DMs between admins. Files, images, urgent flags — everything stays inside the platform.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 rounded-2xl border bg-white overflow-hidden"
           style={{ borderColor: "#E2E8F0", minHeight: 600 }}>
        {/* Conversation list */}
        <aside className="border-r flex flex-col" style={{ borderColor: "#F1F5F9", maxHeight: 720 }}>
          <div className="p-3 border-b" style={{ borderColor: "#F1F5F9" }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94A3B8" }} />
              <Input
                placeholder="Search messages…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                className="pl-9 h-9"
                data-testid="chat-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5" data-testid="conversation-list">
            {loadingConvos ? (
              <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>
            ) : conversations.length === 0 ? (
              <div className="text-xs text-center py-8" style={{ color: "#94A3B8" }}>No conversations yet.</div>
            ) : (
              conversations.map((c) => (
                <ConversationItem
                  key={c.peer.id}
                  convo={c}
                  active={activePeer === c.peer.id}
                  onClick={() => { setActivePeer(c.peer.id); setSearchResults(null); }}
                />
              ))
            )}
          </div>
        </aside>

        {/* Main thread */}
        <section className="flex flex-col" style={{ maxHeight: 720 }}>
          {searchResults !== null ? (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-sm font-semibold" style={{ color: "#0B2545" }}>
                  Search results for &quot;{query}&quot; ({searchResults.length})
                </h3>
                <Button variant="ghost" size="sm" onClick={() => { setSearchResults(null); setQuery(""); }} data-testid="chat-search-close">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {searchResults.length === 0 ? (
                <div className="text-sm text-center py-10" style={{ color: "#94A3B8" }}>No matches.</div>
              ) : (
                <ul className="space-y-2" data-testid="chat-search-results">
                  {searchResults.map((m) => (
                    <li key={m.id} className="rounded-xl border p-3" style={{ borderColor: "#E2E8F0" }}>
                      <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "#64748B" }}>
                        <span className="font-semibold" style={{ color: "#0B2545" }}>{m.sender === me?.id ? "You" : m.sender}</span>
                        <span>→</span>
                        <span>{m.recipient === me?.id ? "You" : m.recipient}</span>
                        <span>·</span>
                        <span>{fmtTime(m.created_at)}</span>
                      </div>
                      <p className="text-sm" style={{ color: "#0B2545" }}>{m.body || `📎 ${m.attachment_name}`}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : !activePeer ? (
            <div className="flex-1 grid place-items-center p-10 text-center" style={{ color: "#64748B" }}>
              <div>
                <p className="font-medium" style={{ color: "#0B2545" }}>Pick a conversation to start chatting.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-5 py-3 border-b flex items-center gap-3" style={{ borderColor: "#F1F5F9" }} data-testid="chat-header">
                <Avatar name={activeConvo?.peer.name} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base font-semibold" style={{ color: "#0B2545" }}>{activeConvo?.peer.name || activePeer}</div>
                  <div className="text-[10px] uppercase tracking-wider" style={{ color: "#7A5E12" }}>{activeConvo?.peer.role}</div>
                </div>
              </div>
              {/* Messages */}
              <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="chat-thread"
                   style={{ background: "linear-gradient(180deg, #FBFCFE 0%, #FFFFFF 100%)" }}>
                {loadingMsgs ? (
                  <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>
                ) : messages.length === 0 ? (
                  <div className="text-sm text-center py-10" style={{ color: "#94A3B8" }}>
                    No messages yet. Say hi 👋
                  </div>
                ) : (
                  messages.map((m) => (
                    <MessageBubble key={m.id} msg={m} isMe={m.sender === me?.id} peerName={activeConvo?.peer.name} />
                  ))
                )}
              </div>
              {/* Composer */}
              <div className="border-t p-3" style={{ borderColor: "#F1F5F9" }} data-testid="chat-composer">
                {draftFile && (
                  <div className="mb-2 inline-flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5"
                       style={{ background: "#F1F5F9", color: "#0B2545" }} data-testid="chat-attachment-preview">
                    {isImageMime(draftFile.type) ? <ImageIcon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                    <span className="truncate max-w-[200px]">{draftFile.name}</span>
                    <span style={{ color: "#94A3B8" }}>· {Math.round(draftFile.size / 1024)} KB</span>
                    <button onClick={() => { setDraftFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                            className="ml-1" style={{ color: "#9B1C1C" }} data-testid="chat-attachment-remove">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <Textarea
                    ref={composerRef}
                    rows={1}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={`Message ${activeConvo?.peer.name || ""}…`}
                    className="resize-none"
                    style={{ minHeight: 40, maxHeight: 160 }}
                    data-testid="chat-input"
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    accept="image/*,application/pdf,.doc,.docx,.txt,.csv,.xlsx,.zip"
                    onChange={(e) => setDraftFile(e.target.files?.[0] || null)}
                    data-testid="chat-file-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                          className="h-10 px-2.5" style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                          data-testid="chat-attach-btn">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={urgent ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUrgent((v) => !v)}
                    className="h-10 px-2.5"
                    style={{
                      borderColor: urgent ? "#9B1C1C" : "#E2E8F0",
                      background: urgent ? "rgba(155,28,28,0.10)" : "#FFFFFF",
                      color: urgent ? "#9B1C1C" : "#0B2545",
                    }}
                    title="Mark as urgent"
                    data-testid="chat-urgent-btn"
                  >
                    <AlertCircle className="h-4 w-4" />
                  </Button>
                  <Button onClick={onSend} disabled={sending || (!draft.trim() && !draftFile)}
                          className="h-10" style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="chat-send">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: "#94A3B8" }}>
                  Press Enter to send, Shift+Enter for a new line. Files up to 25 MB.
                </p>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
